import React, { useState } from 'react';
import { ShieldAlert, X, UserCheck, AlertCircle } from 'lucide-react';
import { Instrument } from '../types/instruments';
import { OverrideConfirmationModal } from './OverrideConfirmationModal';
interface ManualOverrideFormProps {
  instrument: Instrument;
  onResolve: (reason: string, notes: string) => void;
  onCancel: () => void;
}
export function ManualOverrideForm({
  instrument,
  onResolve,
  onCancel
}: ManualOverrideFormProps) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [witnessed, setWitnessed] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const reasons = ['Tool Damaged / Removed from Service', 'Discarded in Biohazard Waste', 'Confirmed Off-Tray (Sterile Field)', 'Sent Separately to Sterilization', 'Other / Detection Error'];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason && notes.length >= 20 && witnessed) {
      setShowConfirmation(true);
    }
  };
  const handleConfirmOverride = () => {
    setShowConfirmation(false);
    onResolve(reason, notes);
  };
  const isValid = reason && notes.length >= 20 && witnessed;
  return <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-id="element-226">
        <div className="w-full max-w-2xl bg-surface border border-border rounded-modal shadow-2xl overflow-hidden" data-id="element-227">
          {/* Header */}
          <div className="bg-surface-2 p-6 border-b border-border flex items-center justify-between" data-id="element-228">
            <div className="flex items-center gap-3" data-id="element-229">
              <div className="p-2 bg-warning/10 rounded-input border border-warning/30" data-id="element-230">
                <ShieldAlert className="w-6 h-6 text-warning" data-id="element-231" />
              </div>
              <h2 className="text-xl font-bold text-text-primary" data-id="element-232">
                Manual Override Resolution
              </h2>
            </div>
            <button onClick={onCancel} className="text-text-secondary hover:text-text-primary transition-colors" data-id="element-233">
              <X className="w-6 h-6" data-id="element-234" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto" data-id="element-235">
            {/* Instrument Info */}
            <div className="bg-surface-2 rounded-card p-4 mb-6 border border-border" data-id="element-236">
              <div className="flex items-center justify-between" data-id="element-237">
                <div data-id="element-238">
                  <div className="text-sm text-text-secondary mb-1 uppercase tracking-wide font-bold" data-id="element-239">
                    Instrument
                  </div>
                  <div className="text-lg font-bold text-text-primary" data-id="element-240">
                    {instrument.name}
                  </div>
                  <div className="text-sm text-text-secondary" data-id="element-241">
                    Slot {instrument.slot} • {instrument.category}
                  </div>
                </div>
                <div className="text-critical font-bold flex items-center gap-2" data-id="element-242">
                  <AlertCircle className="w-5 h-5" data-id="element-243" />
                  Missing
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-244">
              {/* Reason Selection */}
              <div data-id="element-245">
                <label className="block text-sm font-bold text-text-primary mb-3 uppercase tracking-wide" data-id="element-246">
                  Select Reason for Override
                </label>
                <div className="space-y-2" data-id="element-247">
                  {reasons.map(r => <label key={r} className={`
                        flex items-center p-4 rounded-input border cursor-pointer transition-all
                        ${reason === r ? 'bg-info/10 border-info text-text-primary' : 'bg-surface-2 border-border text-text-secondary hover:bg-surface'}
                      `} data-id="element-248">
                      <input type="radio" name="reason" value={r} checked={reason === r} onChange={e => setReason(e.target.value)} className="w-5 h-5 text-info" data-id="element-249" />
                      <span className="ml-3 font-medium" data-id="element-250">{r}</span>
                    </label>)}
                </div>
              </div>

              {/* Notes */}
              <div data-id="element-251">
                <label className="block text-sm font-bold text-text-primary mb-2 uppercase tracking-wide" data-id="element-252">
                  Clinical Notes (minimum 20 characters)
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe what happened to the instrument and why it cannot be accounted for..." className="w-full h-24 bg-surface-2 border border-border rounded-input p-3 text-text-primary placeholder-text-secondary focus:ring-2 focus:ring-info focus:border-transparent resize-none" data-id="element-253" />
                <div className={`text-xs mt-1 ${notes.length >= 20 ? 'text-success' : 'text-text-secondary'}`} data-id="element-254">
                  {notes.length}/20 characters{' '}
                  {notes.length >= 20 ? '✓' : 'required'}
                </div>
              </div>

              {/* Witness Verification */}
              <div className="bg-surface-2 border border-border rounded-card p-4" data-id="element-255">
                <div className="flex items-center justify-between" data-id="element-256">
                  <div className="flex items-center gap-3" data-id="element-257">
                    <div className={`p-2 rounded-input ${witnessed ? 'bg-success/10 border border-success/30' : 'bg-background border border-border'}`} data-id="element-258">
                      <UserCheck className={`w-5 h-5 ${witnessed ? 'text-success' : 'text-text-secondary'}`} data-id="element-259" />
                    </div>
                    <div data-id="element-260">
                      <div className="font-bold text-text-primary" data-id="element-261">
                        Witness Verification Required
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-262">
                        Second person must confirm override
                      </div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setWitnessed(!witnessed)} className={`
                      px-5 py-2.5 rounded-input font-bold text-sm transition-colors
                      ${witnessed ? 'bg-success text-white hover:bg-green-500' : 'bg-surface-2 text-text-secondary border border-border hover:bg-surface'}
                    `} data-id="element-263">
                    {witnessed ? '✓ Verified' : 'Verify'}
                  </button>
                </div>
              </div>

              {/* Audit Notice */}
              <div className="bg-info/5 border border-info/30 rounded-input p-3" data-id="element-264">
                <p className="text-xs text-text-secondary" data-id="element-265">
                  This override will be logged with timestamp, user ID, witness
                  ID, and reason for audit compliance.
                </p>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4" data-id="element-266">
                <button type="button" onClick={onCancel} className="flex-1 px-6 py-3 rounded-input font-bold text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors border border-border" data-id="element-267">
                  Cancel
                </button>
                <button type="submit" disabled={!isValid} className={`
                    flex-1 px-8 py-3 rounded-input font-bold text-white shadow-lg transition-all
                    ${isValid ? 'bg-info hover:bg-blue-500' : 'bg-surface-2 text-text-secondary cursor-not-allowed border border-border'}
                  `} data-id="element-268">
                  Submit Override
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showConfirmation && <OverrideConfirmationModal instrument={instrument} reason={reason} notes={notes} onConfirm={handleConfirmOverride} onCancel={() => setShowConfirmation(false)} data-id="element-269" />}
    </>;
}